import { createReducer } from '@reduxjs/toolkit'
import {
  setHistory,
  undo,
  redo,
  updateHistory,
  resetUpdatedTime,
} from '../actions/history'
import { ConvertData } from '../models/Construction'

export enum ActionType {
  DELETE,
  ADD,
  MOVE,
  EDIT,
}

export interface UndoRedoState {
  convertData: ConvertData
  items?: {
    itemId: string
    actionType: ActionType
  }[]
  isBatch?: boolean
}

interface HistoryState {
  state: UndoRedoState
  undoStack: UndoRedoState[]
  redoStack: UndoRedoState[]
  canUndo: boolean
  canRedo: boolean
  updatedAt?: string
}

const initialState: HistoryState = {
  state: {
    convertData: {
      building: {
        storeys: [],
        roofs: [],
        stairs: [],
        foundations: [],
        has_elements: {
          exterior_walls: false,
          interior_walls: false,
          doors: false,
          windows: false,
          rooms: false,
          roof: false,
        },
        has_structural: false,
        floor_area: 0,
        lot_area: 0,
      },
      message: '',
      context_id: '',
      json_s3_key: '',
    },
  },
  undoStack: [],
  redoStack: [],
  canUndo: false,
  canRedo: false,
}

const historyReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(setHistory, (state, action) => {
      state.state = action.payload
      state.undoStack = []
      state.redoStack = []
      state.canUndo = state.undoStack.length > 0
      state.canRedo = state.redoStack.length > 0
      state.updatedAt = initialState.updatedAt
    })
    .addCase(undo, (state) => {
      if (state.undoStack.length > 0) {
        const previousState = state.undoStack.pop()
        state.redoStack.push({
          ...state.state,
          items: previousState?.items,
        })
        state.state = {
          ...previousState!,
          items: undefined,
        }
      }

      state.canUndo = state.undoStack.length > 0
      state.canRedo = state.redoStack.length > 0
      state.updatedAt = new Date().toString()
    })
    .addCase(redo, (state) => {
      if (state.redoStack.length > 0) {
        const nextState = state.redoStack.pop()
        state.undoStack.push({
          ...state.state,
          items: nextState?.items,
        })
        state.state = {
          ...nextState!,
          items: undefined,
        }
      }

      state.canUndo = state.undoStack.length > 0
      state.canRedo = state.redoStack.length > 0
      state.updatedAt = new Date().toString()
    })
    .addCase(updateHistory, (state, action) => {
      if (action.payload.isBatch) {
        if (!state.undoStack[state.undoStack.length - 1].items) {
          state.undoStack[state.undoStack.length - 1].items = []
        }
        if (action.payload.items) {
          state.undoStack[state.undoStack.length - 1].items = [
            ...state.undoStack[state.undoStack.length - 1].items!,
            ...action.payload.items,
          ]
        }
        state.undoStack[state.undoStack.length - 1].convertData = {
          ...action.payload.convertData,
          ...state.undoStack[state.undoStack.length - 1].convertData,
        }
      } else {
        state.undoStack.push({
          ...state.state,
          items: action.payload.items,
        })
        state.state = {
          ...action.payload,
          items: undefined,
        }
        state.redoStack = []
      }

      state.canUndo = state.undoStack.length > 0
      state.canRedo = state.redoStack.length > 0
      state.updatedAt = new Date().toString()
    })
    .addCase(resetUpdatedTime, (state, action) => {
      state.updatedAt = initialState.updatedAt
    })
})

export default historyReducer
